import { Input, Select, TextArea } from '@components'
import { ACCOUNT_TYPES, getVatRateLabelOptions, VatRateLabel } from '@constants'
import { hasHistoryBeforeNoCFO, isVATObliged, useBusinessContext } from '@context'
import { useLocalStorage, useTheme } from '@hooks'
import {
  BusinessCountry,
  createAccount,
  IAccount,
  updateAccount,
  useBusinessHasHeaders,
  useCurrentUser
} from '@query'
import { setAPIErrors } from '@utils'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useMemo } from 'react'
import { Control, Controller, useForm, useWatch } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { IoMdCloseCircleOutline } from 'react-icons/io'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'
import { AccountPathVisualizer } from './AccountPathVisualizer'
import dayjs from 'dayjs'
import { TranslatedInput } from '@components/TranslatedInput'
import { useVATCodes } from '@root/query/hooks/useVATCodes'
import ReactMarkdown from 'react-markdown'
import { HeaderSelector } from '@root/components/uas/HeaderSelector'

interface Props {
  account?: IAccount
  onClose?: () => void
  onCreate?: (data: IAccount) => void
}

const getFilteredAccountTypeOptions = (accountNumber: number, businessCountry: BusinessCountry) => {
  if (!accountNumber) return ACCOUNT_TYPES
  const term = parseInt(accountNumber?.toString().charAt(0), 10)

  return ACCOUNT_TYPES.filter(option => {
    if (businessCountry === BusinessCountry.FI) {
      if (term === 1) return option.value.startsWith('ASS')
      if (term === 2) return option.value.startsWith('LIA')
      if (term === 3) return option.value.startsWith('REV')
      return option.value.startsWith('REV') || option.value.startsWith('EXP')
    }

    if (businessCountry === BusinessCountry.DE) {
      if (term === 0) return option.value.startsWith('ASS')
      if (term === 1) return option.value.startsWith('ASS')
      if (term === 2) return option.value.startsWith('LIA')
      if (term === 3) return option.value.startsWith('LIA')
      if (term === 4) return option.value.startsWith('REV')
      return option.value.startsWith('EXP')
    }
  })
}

export const AccountForm: React.FC<Props> = ({ account, onClose, onCreate }) => {
  const { i18n } = useTranslation()
  const { data: user } = useCurrentUser()
  const { data: vatCodeOptions } = useVATCodes()
  const { slug, data: business } = useBusinessContext()
  const { country_config: countryConfig, country: businessCountry } = business
  const vat_obliged = isVATObliged(business)
  const has_history = hasHistoryBeforeNoCFO(business)
  const hasHeaders = useBusinessHasHeaders()
  const queryClient = useQueryClient()
  const isEdit = !!account?.id

  const getDefaultValues = (data?: IAccount) => ({
    header_id: data?.header_id,
    number: data?.number,
    name_translations: data?.name_translations || [{ key: i18n.language, value: '' }],
    description: data?.description,
    default_vat_rate_label: data?.default_vat_rate_label || VatRateLabel.ZERO,
    default_vat_code: data?.default_vat_code,
    type: data?.type,
    opening_balance: data?.opening_balance || 0
  })

  const [t] = useTranslation()
  const {
    register,
    handleSubmit,
    setError,
    reset,
    control,
    formState: { errors }
  } = useForm<Partial<IAccount>>({
    defaultValues: getDefaultValues(account)
  })

  const resetForm = (data: IAccount) => reset(getDefaultValues(data))

  const accountNumber = useWatch({
    control,
    name: 'number'
  })

  const accountTypeOptions = useMemo(() => {
    const options = getFilteredAccountTypeOptions(accountNumber, businessCountry)

    return options
  }, [accountNumber])

  const options = {
    onSuccess: data => {
      resetForm(data)
      toast.success(t('general.changesSaved'))

      queryClient.invalidateQueries({ queryKey: [slug, 'accounts'] })
      queryClient.invalidateQueries({ queryKey: [slug, 'tabular_accounts'] })
      queryClient.invalidateQueries({ queryKey: [slug, 'auto_complete_accounts'] })

      onCreate(data)
      onClose && onClose()
    },
    onError: error => {
      const { status, data } = (error as any) ?? {}
      if (status === 400) {
        setAPIErrors(data, setError)
      }
    }
  }

  const updateMutation = useMutation<IAccount, unknown, Partial<IAccount>>({
    mutationFn: data => updateAccount({ slug, accountId: account.id }, data),
    ...options
  })

  const createMutation = useMutation<IAccount, unknown, Partial<IAccount>>({
    mutationFn: data => createAccount({ slug }, data),
    ...options
  })

  const stripTags = (str = '') => str.replaceAll(/(<\/*[0-9]>)/g, '')

  const getCurrentVatOption = (currentValue: string) =>
    vatCodeOptions?.find(vc => {
      return vc.value === +currentValue // buu conversion :poopemoji:
    })

  const handleSave = async data => {
    if (hasHeaders === undefined) return null

    try {
      // https://github.com/orgs/react-hook-form/discussions/9879#discussioncomment-4883381
      await (isEdit ? updateMutation.mutateAsync(data) : createMutation.mutateAsync(data))
    } catch {
      return null
    }
  }
  // Fix for when submitting the form it doesn't send parent forms:
  // https://github.com/redux-form/redux-form/issues/3701#issuecomment-670471760
  return (
    <div onSubmit={event => event.stopPropagation()}>
      <form id="account-form" onSubmit={handleSubmit(handleSave)}>
        <AccountPathVisualizer currentAccountId={account?.id} control={control} onClose={onClose} />

        {hasHeaders && (
          <Controller
            name="header_id"
            control={control}
            rules={{ required: t('validation.required') }}
            render={({ field }) => (
              <HeaderSelector label={t('accounts.header')} {...field} required={true} />
            )}
          />
        )}

        <Input
          type="number"
          label={t('accounts.number')}
          info={t('accounts.numberInfo')}
          {...register('number', {
            required: t('validation.required') as string,
            min: { value: 1, message: '> 0' },
            max: { value: 9999999, message: '< 9999999' }
          })}
          step={1}
          errors={errors.number}
          required={true}
        />

        <Controller
          name={'name_translations'}
          render={({ field }) => (
            <TranslatedInput
              label={t('accounts.name')}
              info={t('accounts.nameInfo')}
              prioritizedLang={user?.language}
              {...field}
            />
          )}
          control={control}
        />

        <TextArea
          label={t('accounts.description')}
          info={t('accounts.descriptionInfo')}
          {...register('description')}
          errors={errors.description}
        />

        {vat_obliged && (
          <>
            <h4>{t('accounts.vatInfo')}</h4>

            <Select
              id="accountForm-input-vat"
              label={t('accounts.defaultVat')}
              info={t('accounts.defaultVatInfo')}
              errors={errors.default_vat_code}
              {...register('default_vat_code')}
            >
              {vatCodeOptions?.map(({ label, value, info }) => (
                <option key={value} value={value} title={stripTags(info)}>
                  {t(label)}
                </option>
              ))}
            </Select>

            <FieldChangeAware control={control} fieldName="default_vat_code">
              {fieldValue =>
                fieldValue && (
                  <ExtraHint>
                    <label htmlFor="accountForm-input-vats">
                      <ReactMarkdown>{getCurrentVatOption(fieldValue)?.info}</ReactMarkdown>
                    </label>
                  </ExtraHint>
                )
              }
            </FieldChangeAware>

            <Select
              info={t('accounts.defaultVatPercentageInfo')}
              label={t('accounts.defaultVatPercentage')}
              errors={errors.default_vat_rate_label}
              {...register('default_vat_rate_label', {
                required: true
              })}
            >
              {getVatRateLabelOptions({
                countryConfig,
                dateAt: dayjs(),
                t,
                uniqueLabels: true
              }).map(({ label, value }, index) => (
                <option key={`option-${index}`} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </>
        )}

        <h4>{t('accounts.additionalInfo')}</h4>

        <Select
          info={t('accounts.accountTypeInfo')}
          label={t('accounts.accountType')}
          errors={errors.type}
          required={true}
          {...register('type', {
            required: t('validation.required') as string
          })}
        >
          <option hidden={true} value={null}></option>
          {accountTypeOptions.map(({ labelKey, value }) => (
            <option key={`type-${value}`} value={value}>
              {t(labelKey)}
            </option>
          ))}
        </Select>

        {has_history && (
          <Input
            info={t('accounts.initialBalanceInfo')}
            type="number"
            label={t('accounts.initialBalance')}
            step=".01"
            errors={errors.opening_balance}
            {...register('opening_balance', { valueAsNumber: true, min: -999999999 })}
          />
        )}
      </form>
    </div>
  )
}

interface InputTooltipProps {
  control: Control<any>
  fieldName: string
  children: (fieldValue: string) => React.ReactNode
}

const FieldChangeAware: React.FC<InputTooltipProps> = ({ control, fieldName, children }) => {
  const fieldValue = useWatch({ control, name: fieldName })

  return <>{children(fieldValue)}</>
}

const ExtraHint: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { slug } = useBusinessContext()
  const [isVisible, setVisibilityForever] = useLocalStorage(
    `${slug}.accountForm.vatTooltipVisible`,
    true
  )
  const theme = useTheme()

  return (
    <AnimatePresence>
      {isVisible && (
        <HintContainer
          key="hintContainer"
          variants={containerVariants}
          initial="visible"
          exit="hidden"
          animate="visible"
        >
          {children}
          <Info onClick={() => setVisibilityForever(false)}>
            <IoMdCloseCircleOutline
              role="button"
              aria-label="piilota"
              color={theme.colors.neutralWhite}
            />
          </Info>
        </HintContainer>
      )}
    </AnimatePresence>
  )
}

const containerVariants = {
  visible: {
    opacity: 1
  },
  hidden: {
    opacity: 0,
    transition: {
      duration: 0.3
    }
  }
}

const HintContainer = styled(motion.div)(
  ({ theme }) => `
  background: ${theme.colors.menuBg};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing.sm}rem ${theme.spacing.md}rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  color: ${theme.colors.neutralWhite};
  font-size: ${theme.fontSize.md}rem;
  margin-bottom: ${theme.spacing.md}rem;
  position: relative;

  & > label {
    margin-bottom: 0;

    & > a {
      color: ${theme.colors.mutedYellow};
      fontWeight: bold;

    }
  }

  &:before {
    content: "";
    width: 0px;
    height: 0px;
    position: absolute;
    border-left: 10px solid ${theme.colors.menuBg};
    border-right: 10px solid transparent;
    border-top: 10px solid ${theme.colors.menuBg};
    border-bottom: 10px solid transparent;
    right: 50%;
    top: -4px;
    transform: rotate(45deg);
    }

`
)

const Info = styled.span(
  ({ theme }) => `
  align-self: flex-start;
  margin-left: ${theme.spacing.md}rem;
  margin-top: 2px;

  display: flex;
  justify-content: center;
  align-items: center;

  & > svg {
    height: 1.2rem;
    width: 1.2rem;
  }

  &:hover {
    transform: scale(1.1);
    transition: transform 200ms;
  }
`
)
